import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { makeRouter } from '../router'
import App from '../App.vue'
import { getWidgetConfig, setWidgetConfig } from '../runtime/widgetConfig'
import { applyWidgetTheme } from '../runtime/applyWidgetTheme'
import { initProjectOnce } from '../runtime/init-project.client'
import { initUploadsRunnerOnce } from '../runtime/uploads-runner.client'

import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client/core'
import { DefaultApolloClient } from '@vue/apollo-composable'

import type { WidgetProps, EntryContext } from '../runtime/widgetConfig.ts'

export type EditorAppOptions = {
    mountEl: Element
    props: WidgetProps
    dev?: boolean
}

function buildEntryQuery(entry?: EntryContext) {
    const q: Record<string, string> = {}

    if (entry?.parentId) q.parent = String(entry.parentId)
    if (entry?.parentName) q.parentName = entry.parentName

    if (entry?.categoryId) q.category = String(entry.categoryId)
    if (entry?.categoryName) q.categoryName = entry.categoryName

    if (entry?.productId) q.product = String(entry.productId)
    if (entry?.productName) q.productName = entry.productName

    return q
}

function buildBrowserSearchQuery(url: URL | null): Record<string, string> {
    if (!url) return {}
    const out: Record<string, string> = {}
    for (const [key, value] of url.searchParams.entries()) {
        if (!key) continue
        out[key] = value
    }
    return out
}

function resolveInitialRoutePath(url: URL | null, routerMode: any, routerBase?: string) {
    if (!url) return '/'
    if (routerMode !== 'web') {
        const hashPath = url.hash.slice(1).split('?')[0] || '/'
        return hashPath || '/'
    }
    const base = String(routerBase || '/').replace(/\/+$/, '') || ''
    const pathname = String(url.pathname || '/')
    if (!base || base === '/') return pathname || '/'
    if (pathname.startsWith(base + '/')) return pathname.slice(base.length) || '/'
    if (pathname === base) return '/'
    return pathname || '/'
}

export async function createEditorApp({ mountEl, props }: EditorAppOptions) {
   // mountEl.innerHTML = '<pre style="white-space:pre-wrap;padding:16px;background:#fff;color:#222;">boot: start</pre>'
    // 1) Config (nur runtime-relevantes)
    setWidgetConfig({
        editorBaseUrl: props.editorBaseUrl,
        cartBackendBaseUrl: props.cartBackendBaseUrl,
        graphqlUrl: props.graphqlUrl,
        authToken: props.authToken,
        quickUploadTargetUrl: props.quickUploadTargetUrl,
        quickUploadProjectCode: props.quickUploadProjectCode,
        quickUploadSizeVariantId: props.quickUploadSizeVariantId,
        quickUploadSizeTitle: props.quickUploadSizeTitle,
        quickUploadSizeWidthMm: props.quickUploadSizeWidthMm,
        quickUploadSizeHeightMm: props.quickUploadSizeHeightMm,
        simpleUploadFlow: props.simpleUploadFlow,
        simpleEditorFlow: props.simpleEditorFlow,
        minimalUploadFlow: props.minimalUploadFlow,
        quickUploadQuantity: props.quickUploadQuantity,
        quickUploadGrayscale: props.quickUploadGrayscale,
        quickUploadCropRequired: props.quickUploadCropRequired,
        quickUploadRecoveryState: props.quickUploadRecoveryState,
        mobileUploadUrl: props.mobileUploadUrl,
        mercureHubUrl: props.mercureHubUrl,
        cartUrl: props.cartUrl,
        shippingCostsUrl: props.shippingCostsUrl,
        overviewUrl: props.overviewUrl,
        fontFamily: props.fontFamily,
        fontSizeBase: props.fontSizeBase,
        textColor: props.textColor,
        mutedColor: props.mutedColor,
        accentColor: props.accentColor,
        backgroundColor: props.backgroundColor,
        borderColor: props.borderColor,
    })
    applyWidgetTheme(mountEl, getWidgetConfig())

    function makeApollo() {
        const { graphqlUrl, authToken } = getWidgetConfig() as any
        return new ApolloClient({
            link: createHttpLink({
                uri: graphqlUrl,
                fetch,
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
            }),
            cache: new InMemoryCache(),
        })
    }

    // 2) App + Pinia
    // -> entry als Prop reinreichen (optional, aber praktisch fürs Entry-Screen)
    const uiProps = {
        entry: props.entry ?? null,
    }

    const app = createApp(App)
    const pinia = createPinia()
    pinia.use(piniaPluginPersistedstate)
    app.use(pinia)

    // 3) Apollo
    const apollo = makeApollo()
    app.provide(DefaultApolloClient, apollo)

    // 4) Router
    const router = makeRouter(props)
    app.use(router)

    // ✅ Priorität: quickUpload > initialRoute > default
    const entryQuery = buildEntryQuery(props.entry)
    const browserUrl = typeof window !== 'undefined' ? new URL(window.location.href) : null
    const browserQuery = buildBrowserSearchQuery(browserUrl)
    const quickUploadFromUrl = browserUrl?.searchParams.get('quickUpload') === '1'
    const mobileUploadFromPath = browserUrl?.pathname.replace(/\/+$/, '').endsWith('/mobile-upload')
    const initialRoutePath = resolveInitialRoutePath(browserUrl, props.routerMode, props.routerBase)

    if (mobileUploadFromPath) {
        router.replace({
            path: '/mobile-upload',
            query: {
                data: browserUrl?.searchParams.get('data') || browserUrl?.searchParams.get('payload') || '',
            },
        })
    } else if (quickUploadFromUrl) {
        router.replace({
            path: '/quick-upload',
            query: {
                category: browserUrl?.searchParams.get('category') || browserUrl?.searchParams.get('categoryId') || '',
                product: browserUrl?.searchParams.get('product') || browserUrl?.searchParams.get('productId') || '',
                projectCode: browserUrl?.searchParams.get('projectCode') || '',
                phone: browserUrl?.searchParams.get('phone') || '1',
            },
        })
    } else if (props.quickUpload) {
        router.replace({
            path: '/quick-upload',
            query: {
                ...browserQuery,
                ...entryQuery,
                ...(props.quickUploadProjectCode ? { projectCode: props.quickUploadProjectCode } : {}),
                ...(props.quickUploadSizeVariantId ? { sizeVariantId: props.quickUploadSizeVariantId } : {}),
                ...(props.quickUploadSizeTitle ? { sizeTitle: props.quickUploadSizeTitle } : {}),
                ...(props.quickUploadSizeWidthMm ? { sizeWidthMm: String(props.quickUploadSizeWidthMm) } : {}),
                ...(props.quickUploadSizeHeightMm ? { sizeHeightMm: String(props.quickUploadSizeHeightMm) } : {}),
                ...(props.simpleUploadFlow ? { simpleFlow: '1' } : {}),
                ...(props.simpleEditorFlow ? { simpleEditorFlow: '1' } : {}),
                ...(props.minimalUploadFlow ? { minimalFlow: '1' } : {}),
                ...(props.quickUploadQuantity != null ? { quantity: String(props.quickUploadQuantity) } : {}),
                ...(props.quickUploadGrayscale != null ? { grayscale: String(props.quickUploadGrayscale) } : {}),
                ...(props.quickUploadCropRequired != null ? { cropRequired: String(props.quickUploadCropRequired) } : {}),
                ...(props.quickUploadRecoveryState ? { recoveryState: props.quickUploadRecoveryState } : {}),
            }, // <- parent/category/product/name direkt aus props.entry
        })
    } else if (Object.keys(entryQuery).length || (Object.keys(browserQuery).length && (initialRoutePath === '/' || initialRoutePath === ''))) {
        // Nur zur Übersicht navigieren wenn kein Hash-Pfad gesetzt ist (Erststart).
        // Bei Reload auf /multi oder /single nicht überschreiben.
        if (initialRoutePath === '/' || initialRoutePath === '') {
            router.replace({ path: '/', query: { ...browserQuery, ...entryQuery } })
        }
    } else if (props.initialRoute && (initialRoutePath === '/' || initialRoutePath === '')) {
        router.replace(props.initialRoute)
    }

    // 5) Init
    mountEl.innerHTML = '<pre style="white-space:pre-wrap;padding:16px;background:#fff;color:#222;">boot: loading…</pre>'

    // router.isReady() is independent of project/runner init — run them concurrently.
    // The uploads runner depends on the project being initialized, so keep that ordering sequential.
    const [runner] = await Promise.all([
        initProjectOnce(pinia).then(() => initUploadsRunnerOnce({ pinia })),
        router.isReady(),
    ])

    app.mount(mountEl)

    return {
        app,
        router,
        pinia,
        apollo,
        runner,
        unmount() {
            app.unmount()
        },
    }
}
